import { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import {
  BatchCommandHistoryResponse,
  CommandHistoryResponse,
  CommandInfoResponse,
  DownloadCSVResponse,
  ExecutionHistoryDetailsResponse,
  ExecutionHistoryDetailsResponseModified,
  GetDeviceIdentifiersFromBatchIdResponse,
  GetSourceListResponse,
  GetUserEmailListResponse,
  ResponseBaseWithOutPagination,
  UploadCSVFileResponse
} from '../../types';
import {
  CommandInfoRecordTransformed,
  ExecuteCommandPayload,
  ExecutionHistoryDetailsRecordModified
} from '../../types/records/command-execution';
import { CACHING_TIME } from '@/store/utils';
import { DownloadCSVRecord } from '../../types/records/download-csv';

type ProgressUploadPayload = {
  formData: FormData;
  onProgress: (progress: number) => void;
  signal: AbortSignal;
};

export const commandExecutionEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      NonNullable<unknown>,
      FetchBaseQueryMeta
    >,
    string,
    'hesApi'
  >
) => ({
  getCommandInfo: builder.query<
    CommandInfoRecordTransformed[],
    { searchParams: string }
  >({
    query: ({ searchParams }) => ({
      url: `/command-execution/command-info${searchParams}`,
      method: 'GET'
    }),
    providesTags: ['CommandList'],
    keepUnusedDataFor: CACHING_TIME,
    transformResponse: (
      response: CommandInfoResponse
    ): CommandInfoRecordTransformed[] => {
      return response.data.records.map((record) => ({
        ...record,
        label: record.name,
        value: record.name
      }));
    }
  }),
  getBatchCommandExecutionHistory: builder.query<
    BatchCommandHistoryResponse,
    { searchParams: string }
  >({
    query: ({ searchParams }) => ({
      url: `/command-execution/batch-execution-history${searchParams}`,
      method: 'GET'
    }),
    providesTags: ['batchExecutionHistory']
  }),
  getCommandExecutionHistory: builder.query<
    CommandHistoryResponse,
    { searchParams: string }
  >({
    query: ({ searchParams }) => ({
      url: `/command-execution/execution-history${searchParams}`,
      method: 'GET'
    })
  }),
  executeCommand: builder.mutation<
    ResponseBaseWithOutPagination<null>,
    ExecuteCommandPayload
  >({
    query: (data) => ({
      url: '/command-execution/execute-command',
      method: 'POST',
      body: data
    }),
    invalidatesTags: ['batchExecutionHistory']
  }),
  uploadFileWithProgress: builder.mutation<
    UploadCSVFileResponse,
    ProgressUploadPayload
  >({
    queryFn: async ({ formData, onProgress, signal }) => {
      const baseUrl = `${import.meta.env.VITE_HES_BASE_URL}/${
        import.meta.env.VITE_HES_API_VERSION
      }/device-management/upload-device-identifier-list`;

      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', baseUrl);

        const token = localStorage.getItem('hes_token');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.upload.onprogress = function (e: ProgressEvent<EventTarget>) {
          const percentComplete = Math.ceil((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ data: JSON.parse(xhr.responseText) });
          } else {
            resolve({
              error: {
                status: xhr.status,
                data: xhr.responseText
              }
            });
          }
        };

        xhr.onerror = () => {
          resolve({
            error: {
              status: xhr.status,
              data: xhr.responseText || 'Network Error'
            }
          });
        };

        signal.addEventListener('abort', () => {
          xhr.abort();
          resolve({
            error: {
              status: 499,
              data: 'Upload aborted by user.'
            }
          });
        });

        xhr.send(formData);
      });
    }
  }),

  uploadCSVfile: builder.mutation<UploadCSVFileResponse, FormData>({
    query: (data) => ({
      url: 'device-management/upload-device-identifier-list',
      method: 'POST',
      body: data
    })
  }),
  getCommandExecutionHistoryDetails: builder.query<
    ExecutionHistoryDetailsResponseModified,
    { searchParams: string }
  >({
    query: ({ searchParams }) => ({
      url: `/command-execution/command-response${searchParams}`,
      method: 'GET'
    }),
    transformResponse: (
      response: ExecutionHistoryDetailsResponse
    ): ExecutionHistoryDetailsResponseModified => {
      const records = response.data.records.map((record) => {
        const { response, pendingStatusReason, ...rest } = record;
        let finalResponse: ExecutionHistoryDetailsRecordModified = { ...rest };
        if (pendingStatusReason)
          finalResponse['pendingStatusReason'] = pendingStatusReason;
        if (response) {
          const { responseData, ...other } = response;
          finalResponse = { ...finalResponse, ...other };
          if (responseData) {
            finalResponse = { ...finalResponse, ...responseData };
          }
        }

        return finalResponse;
      });

      return {
        ...response,
        data: {
          ...response.data,
          records,
          obisCodeMap: response.data.obisCodeMap || [],
          keyOrder: response.data.keyOrder || []
        }
      };
    }
  }),
  downloadCommandResponsePayload: builder.query<
    DownloadCSVRecord,
    { searchQuery: string }
  >({
    query: ({ searchQuery }) => ({
      url: `command-execution/download-report/command-response${searchQuery}`,
      method: 'GET',
      responseHandler: async (response) => {
        const contentType = response.headers.get('content-type');

        // If the content type is JSON, parse the response as JSON
        if (contentType?.includes('application/json')) {
          return await response.json(); // Parse and return JSON
        }

        // Otherwise, treat it as a Blob (for files like CSV)
        return await response.blob(); // Handle as Blob (binary data)
      }
    }),
    transformResponse: (
      response: Blob | DownloadCSVResponse,
      meta
    ): DownloadCSVRecord => {
      const contentType = meta?.response?.headers?.get('content-type');
      console.log(response);
      console.log(contentType);

      // Check if the content type is CSV or text, indicating a file download
      if (
        contentType?.includes('text/csv') ||
        contentType?.includes('application/octet-stream') ||
        contentType?.includes('application/pdf')
      ) {
        // If the response is a Blob (CSV file), return it as a csvBlob
        return { csvBlob: response as Blob };
      }

      // If it's a JSON response, handle it as DownloadCSVResponse
      if ('data' in response) {
        const downloadResponse = response as DownloadCSVResponse;
        const url = downloadResponse?.data?.records[0]?.url;
        return { url };
      }

      // If neither case matches, throw an error or handle accordingly
      throw new Error('Unexpected response format');
    }
  }),
  getDeviceIdentifiersFromBatchId: builder.query<
    GetDeviceIdentifiersFromBatchIdResponse,
    string
  >({
    query: (paramString) => ({
      url: `command-execution/device-identifiers-for-batch-id/${paramString}`
    }),
    keepUnusedDataFor: 0
  }),
  getUserEmailIdList: builder.query<
    GetUserEmailListResponse,
    { limit: number }
  >({
    query: (params) => ({
      url: 'command-execution/user-list?user_email_id=.com',
      params: params
    })
  }),
  getSourceList: builder.query<GetSourceListResponse, void>({
    query: () => ({
      url: 'command-execution/source-list'
    })
  }),
  downloadBatchExecHistory: builder.query<DownloadCSVRecord, string>({
    query: (batchId) => ({
      url: `command-execution/download-report/batch-execution-history/${batchId}`,
      responseHandler: async (response) => {
        const contentType = response.headers.get('content-type');

        // If the content type is JSON, parse the response as JSON
        if (contentType?.includes('application/json')) {
          return await response.json(); // Parse and return JSON
        }

        // Otherwise, treat it as a Blob (for files like CSV)
        return await response.blob(); // Handle as Blob (binary data)
      }
    }),
    transformResponse: (
      response: Blob | DownloadCSVResponse,
      meta
    ): DownloadCSVRecord => {
      const contentType = meta?.response?.headers?.get('content-type');
      console.log(response);
      console.log(contentType);

      // Check if the content type is CSV or text, indicating a file download
      if (
        contentType?.includes('text/csv') ||
        contentType?.includes('application/octet-stream')
      ) {
        // If the response is a Blob (CSV file), return it as a csvBlob
        return { csvBlob: response as Blob };
      }

      // If it's a JSON response, handle it as DownloadCSVResponse
      if ('data' in response) {
        const downloadResponse = response as DownloadCSVResponse;
        const url = downloadResponse?.data?.records[0]?.url;
        return { url };
      }

      // If neither case matches, throw an error or handle accordingly
      throw new Error('Unexpected response format');
    }
  })
});
